import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  X, Search, Eye, Copy, Pencil, Trash2, Files, ExternalLink,
  ChevronLeft, ChevronRight, ArrowDownUp,
} from "lucide-react";

import { useProposalStore } from "../../store/useProposalStore";
import { useAuthStore } from "../../store/useAuthStore";

const nameOf = (v: any): string =>
  typeof v === "object" && v ? v.fullName || v.name || "" : "";
const idOf = (v: any): string =>
  typeof v === "object" && v ? v._id || "" : v || "";
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

const TEMPLATE_STYLE: Record<string, string> = {
  modern: "bg-blue-50 text-blue-700",
  classic: "bg-emerald-50 text-emerald-700",
  minimal: "bg-slate-100 text-slate-600",
};

const PAGE_SIZE = 12;

const ProposalList = () => {
  const navigate = useNavigate();
  const { proposals, isFetchingProposals, deleteProposal, duplicateProposal, fetchProposals, fetchStats } =
    useProposalStore();
  const { authUser } = useAuthStore();
  const isBD = authUser?.role === "Business Developer";

  // Filters / search / sort.
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [bdId, setBdId] = useState("");
  const [account, setAccount] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState<"latest" | "oldest">("latest");
  const [page, setPage] = useState(1);

  const bdOptions = useMemo(() => {
    const m = new Map<string, string>();
    proposals.forEach((p: any) => { const id = idOf(p.createdBy); if (id) m.set(id, nameOf(p.createdBy) || "Unknown BD"); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [proposals]);

  const accountOptions = useMemo(() => {
    const m = new Map<string, string>();
    proposals.forEach((p: any) => { const id = idOf(p.upworkAccount); if (id) m.set(id, nameOf(p.upworkAccount) || "Account"); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [proposals]);

  const categoryOptions = useMemo(() => {
    const s = new Set<string>();
    proposals.forEach((p: any) => { if (p.category) s.add(p.category); });
    return Array.from(s);
  }, [proposals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = proposals.filter((p: any) => {
      if (q) {
        const hay = `${p.title || ""} ${p.category || ""} ${nameOf(p.createdBy)} ${nameOf(p.client)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (category && p.category !== category) return false;
      if (bdId && idOf(p.createdBy) !== bdId) return false;
      if (account && idOf(p.upworkAccount) !== account) return false;
      if (from && (!p.createdAt || new Date(p.createdAt) < new Date(from))) return false;
      if (to) {
        const end = new Date(to); end.setHours(23, 59, 59, 999);
        if (!p.createdAt || new Date(p.createdAt) > end) return false;
      }
      return true;
    });
    list.sort((a: any, b: any) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return sort === "latest" ? tb - ta : ta - tb;
    });
    return list;
  }, [proposals, search, category, bdId, account, from, to, sort]);

  const hasFilters = !!(search || category || bdId || account || from || to);
  const clearFilters = () => { setSearch(""); setCategory(""); setBdId(""); setAccount(""); setFrom(""); setTo(""); setPage(1); };

  // Pagination.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Admin "View by BD": the KPI/analytics section follows the selected BD too,
  // so stats and the list always describe the same set of proposals.
  useEffect(() => {
    if (!isBD) fetchStats(bdId ? { bdId } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bdId]);

  const publicUrl = (p: any) => p.shareUrl || `${window.location.origin}/proposal/public/${p.publicToken}`;
  const copyLink = (p: any) => { navigator.clipboard.writeText(publicUrl(p)); toast.success("Public link copied"); };

  const handleDuplicate = async (id: string) => {
    const copy = await duplicateProposal(id);
    if (copy?._id) {
      await Promise.all([fetchProposals(), fetchStats()]);
      navigate(`/proposal-builder?id=${copy._id}`);
    }
  };

  const handleDelete = async (p: any) => {
    if (!window.confirm(`Delete proposal "${p.title || "Untitled"}"? This cannot be undone.`)) return;
    await deleteProposal(p._id);
    fetchStats();
  };

  const selCls = "px-2.5 py-1.5 text-[13px] bg-white border border-outline-variant rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40";

  if (isFetchingProposals) {
    return <div className="p-6 text-center text-gray-500 text-sm">Loading proposals…</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">
            Proposals: <span className="text-primary">{filtered.length}</span>
            {hasFilters && <span className="text-sm font-normal text-on-surface-variant"> of {proposals.length}</span>}
          </h2>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search title, category, BD, client…"
              className="w-64 max-w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-outline-variant text-[13px] outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {categoryOptions.length > 0 && (
            <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className={selCls} title="Filter by category">
              <option value="">All categories</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {!isBD && bdOptions.length > 1 && (
            <select value={bdId} onChange={(e) => { setBdId(e.target.value); setPage(1); }} className={selCls} title="Filter by BD">
              <option value="">All BDs</option>
              {bdOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {accountOptions.length > 0 && (
            <select value={account} onChange={(e) => { setAccount(e.target.value); setPage(1); }} className={selCls} title="Filter by Upwork account">
              <option value="">All accounts</option>
              {accountOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className={selCls} title="From date" />
          <span className="text-on-surface-variant text-sm">–</span>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className={selCls} title="To date" />
          <select value={sort} onChange={(e) => setSort(e.target.value as any)} className={selCls} title="Sort">
            <option value="latest">Latest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          {hasFilters && (
            <button onClick={clearFilters} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container-high rounded-lg">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* List view */}
      {!filtered.length ? (
        <div className="p-8 text-center text-gray-500 text-sm">
          {proposals.length ? "No proposals match the selected filters." : "No proposals found yet."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-outline-variant">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-surface-container-high text-on-surface-variant text-left text-[11px] uppercase tracking-wider">
                <th className="px-4 py-3 font-bold">Proposal</th>
                <th className="px-4 py-3 font-bold">Client / Account</th>
                {!isBD && <th className="px-4 py-3 font-bold">BD</th>}
                <th className="px-4 py-3 font-bold">Category</th>
                <th className="px-4 py-3 font-bold">Template</th>
                <th className="px-4 py-3 font-bold text-center">
                  <span className="inline-flex items-center gap-1"><Eye size={12} /> Views</span>
                </th>
                <th className="px-4 py-3 font-bold">
                  <span className="inline-flex items-center gap-1"><ArrowDownUp size={11} /> Created</span>
                </th>
                <th className="px-4 py-3 font-bold">Updated</th>
                <th className="px-4 py-3 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p: any) => (
                <tr key={p._id} className="border-t border-outline-variant hover:bg-surface-container-low/50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-on-surface truncate max-w-[220px]">{p.title || "Untitled"}</div>
                    <div className="text-[11px] text-on-surface-variant">{p.brand || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant">
                    {nameOf(p.client) || nameOf(p.upworkAccount) || "—"}
                  </td>
                  {!isBD && (
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-6 h-6 rounded-full bg-primary-container text-on-primary-container grid place-items-center text-[10px] font-bold">
                          {(nameOf(p.createdBy) || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span className="truncate max-w-[120px]">{nameOf(p.createdBy) || "—"}</span>
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full bg-surface-container-high text-on-surface-variant text-[11px] font-semibold">
                      {p.category || "Uncategorized"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${TEMPLATE_STYLE[p.template || "modern"] || TEMPLATE_STYLE.minimal}`}>
                      {p.template || "modern"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {p.viewCount ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold" title={p.lastViewedAt ? `Last viewed ${fmtDate(p.lastViewedAt)}` : ""}>
                        {p.viewCount}
                      </span>
                    ) : (
                      <span className="text-[11px] text-outline">Not viewed</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap">{fmtDate(p.createdAt)}</td>
                  <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap">{fmtDate(p.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <a href={publicUrl(p)} target="_blank" rel="noreferrer noopener" title="Preview" className="p-1.5 rounded-md text-outline hover:text-primary hover:bg-surface-container-high">
                        <ExternalLink size={15} />
                      </a>
                      <button onClick={() => copyLink(p)} title="Copy public link" className="p-1.5 rounded-md text-outline hover:text-primary hover:bg-surface-container-high">
                        <Copy size={15} />
                      </button>
                      <button onClick={() => navigate(`/proposal-builder?id=${p._id}`)} title="Edit" className="p-1.5 rounded-md text-outline hover:text-primary hover:bg-surface-container-high">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => handleDuplicate(p._id)} title="Duplicate" className="p-1.5 rounded-md text-outline hover:text-primary hover:bg-surface-container-high">
                        <Files size={15} />
                      </button>
                      <button onClick={() => handleDelete(p)} title="Delete" className="p-1.5 rounded-md text-outline hover:text-error hover:bg-error-container/40">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-[12px] text-on-surface-variant">
          <span>Page {safePage} of {totalPages} · {filtered.length} proposals</span>
          <div className="flex items-center gap-1">
            <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} className="p-1.5 rounded-md border border-outline-variant disabled:opacity-40 hover:bg-surface-container-high"><ChevronLeft size={15} /></button>
            <button disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} className="p-1.5 rounded-md border border-outline-variant disabled:opacity-40 hover:bg-surface-container-high"><ChevronRight size={15} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalList;
